import { Box, MenuItem, Select, Stack, Typography } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import type { ProductMinimal } from '@travelity/api';
import type { TimeOffset } from '@travelity/api/generated';
import {
    formatMinutesToTimeLabel,
    getDurationMinutesFromProduct,
    getScheduleTimeOptions,
    parseStartTimeToMinutes,
    validateSequentialStartTime,
} from './helpers';
import type { PackageFormValues } from '@/views/add-package/add-package.types';

export type StartTimeFieldProps = {
    dayIndex: number;
    productIndex: number;
    product: ProductMinimal & { start_time?: string };
    scheduleInput?: {
        any_time?: boolean;
        start_times?: Array<TimeOffset>;
    };
};

const StartTimeField = ({
    dayIndex,
    productIndex,
    product,
    scheduleInput,
}: StartTimeFieldProps) => {
    const { control, getValues } = useFormContext<PackageFormValues>();
    const options = getScheduleTimeOptions(scheduleInput);

    return (
        <Controller
            name={`products.${dayIndex}.items.${productIndex}.start_time`}
            control={control}
            defaultValue=""
            rules={{
                validate: {
                    required: value => (value ? true : 'Select a start time'),
                    duration: value => {
                        if (!value) return true;
                        const durationMinutes =
                            getDurationMinutesFromProduct(product);
                        return durationMinutes
                            ? true
                            : 'Duration is missing for this product';
                    },
                    sequential: value => {
                        if (!value) return true;
                        if (productIndex === 0) return true;
                        const values = getValues('products');
                        const prevProduct =
                            values?.[dayIndex]?.items?.[productIndex - 1];
                        const prevStart = parseStartTimeToMinutes(
                            prevProduct?.start_time
                        );
                        const prevDuration =
                            getDurationMinutesFromProduct(prevProduct);
                        const currentStart = parseStartTimeToMinutes(value);
                        return validateSequentialStartTime({
                            currentStartMinutes: currentStart,
                            prevStartMinutes: prevStart,
                            prevDurationMinutes: prevDuration,
                        });
                    },
                },
            }}
            render={({ field, fieldState }) => {
                const startMinutes = parseStartTimeToMinutes(field.value);
                const durationMinutes = getDurationMinutesFromProduct(product);
                const endLabel =
                    startMinutes === null || durationMinutes === null
                        ? null
                        : formatMinutesToTimeLabel(
                              startMinutes + durationMinutes
                          );

                return (
                    <Stack direction="column">
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Select
                                {...field}
                                size="small"
                                displayEmpty
                                sx={{ minWidth: 140 }}
                                error={!!fieldState.error}
                                onChange={event =>
                                    field.onChange(event.target.value)
                                }
                            >
                                <MenuItem value="">
                                    <em>Choose time</em>
                                </MenuItem>
                                {options.map(option => (
                                    <MenuItem
                                        key={option.value}
                                        value={option.value}
                                    >
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                            {endLabel && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        ml: 1.5,
                                    }}
                                >
                                    <Typography variant="body2">
                                        Ends
                                    </Typography>
                                    {endLabel}
                                </Box>
                            )}
                        </Box>
                        {fieldState.error && (
                            <Typography
                                variant="caption"
                                color="error"
                                sx={{ maxWidth: '180px' }}
                            >
                                {fieldState.error.message}
                            </Typography>
                        )}
                    </Stack>
                );
            }}
        />
    );
};

export default StartTimeField;
